// /src/components/sampleorders/SampleOrderTable.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  ShoppingCart,
  Eye,
  EyeOff,
  ArrowUpDown,
  Filter,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  SquarePen,
  X,
  Download,
} from "lucide-react";
import { getItemCountsForRequest } from "./sampleOrderItemUtils";
import { filterVisibleSampleOrders } from "../../utils/sampleOrderVisibility";
import * as XLSX from "xlsx";
import { getSampleOrderStatusLabel } from "../../utils/sampleOrderStatusLabels";

/**
 * ✅ UPDATED (Drop-in replacement)
 * - ✅ Account is FIRST column
 * - ✅ Added filter by Status (collapsible filter panel like InventoryTable)
 * - ✅ Status dropdowns are WIDER so full text is visible
 * - ✅ Bulk edit toolbar matches InventoryTable look/feel (prettier, consistent)
 * - ✅ Preserved grouped-one-row-per-order logic + visibility filtering + bulk save banners
 */

const STATUS_OPTIONS = [
  "Pending",
  "Approved",
  "Preparation",
  "Shipped",
  "Delivered",
  "Received",
  "Follow Up",
  "Converted to Order",
  "Cancelled",
  "Completed",
];

const DELIVERY_STAFF_GROUP_ID = "grp_delivery_staff";
const WAREHOUSE_STAFF_GROUP_ID = "grp_warehouse_staff";

const BANNER_AUTO_HIDE_MS = 3500;

// small helper
const safeLower = (v) => (v == null ? "" : String(v).toLowerCase());

const getRestrictedStatusMeta = (status, groupIds = [], canManage = false) => {
  if (!canManage) return { options: STATUS_OPTIONS, disabled: false, note: "" };

  const normalizedGroupIds = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [];
  const isDeliveryStaff = normalizedGroupIds.includes(DELIVERY_STAFF_GROUP_ID);
  const isWarehouseStaff = normalizedGroupIds.includes(WAREHOUSE_STAFF_GROUP_ID);

  if (!isDeliveryStaff && !isWarehouseStaff) {
    return { options: STATUS_OPTIONS, disabled: false, note: "" };
  }

  const currentStatus = status || "Pending";
  const optionSet = new Set([currentStatus]);
  const notes = [];

  if (isWarehouseStaff) {
    notes.push("Warehouse Staff can only move sample orders from Approved to Preparation or Shipped.");
    if (currentStatus === "Approved") {
      optionSet.add("Preparation");
      optionSet.add("Shipped");
    }
  }

  if (isDeliveryStaff) {
    notes.push("Delivery Staff can only move sample orders from Shipped to Delivered.");
    if (currentStatus === "Shipped") {
      optionSet.add("Delivered");
    }
  }

  const options = Array.from(optionSet);
  const hasTransition = options.some((option) => option !== currentStatus);

  return {
    options,
    disabled: !hasTransition,
    note: notes.join(" "),
  };
};

const intersectStatusOptions = (groups, groupIds = [], canManage = false) => {
  if (!groups.length) return STATUS_OPTIONS;
  let shared = null;
  groups.forEach((group) => {
    const meta = getRestrictedStatusMeta(group.status === "MIXED" ? "" : group.status, groupIds, canManage);
    const validOptions = meta.options.filter((option) => STATUS_OPTIONS.includes(option));
    if (shared == null) shared = new Set(validOptions);
    else shared = new Set(validOptions.filter((option) => shared.has(option)));
  });
  return Array.from(shared || STATUS_OPTIONS);
};

const SampleOrderTable = ({
  requests = [],
  companyUsers = [],
  isAdmin,
  currentUserId, // kept for compatibility
  currentUserRole = "",
  currentUserGroupIds = [],
  currentUserAccountIds = [],
  onUpdateRequest,
  onSelectRequest,
  sampleStatusLabelSettings = {},
}) => {
  // Sorting
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Hidden toggle (admin/owner only)
  const [showHidden, setShowHidden] = useState(false);

  // ✅ Filters (now includes status)
  const [filters, setFilters] = useState({
    search: "",
    account: "ALL",
    territory: "ALL",
    status: "ALL",
  });

  // ✅ Collapsible filter panel (InventoryTable style)
  const [showFilters, setShowFilters] = useState(false);

  const resetFilters = () => {
    setFilters({
      search: "",
      account: "ALL",
      territory: "ALL",
      status: "ALL",
    });
  };

  const hasAnyFilter =
    !!filters.search ||
    filters.account !== "ALL" ||
    filters.territory !== "ALL" ||
    filters.status !== "ALL";

  const activeFilterCount = [
    filters.search ? 1 : 0,
    filters.account !== "ALL" ? 1 : 0,
    filters.territory !== "ALL" ? 1 : 0,
    filters.status !== "ALL" ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkAssignedToId, setBulkAssignedToId] = useState("");
  const [bulkTerritoryName, setBulkTerritoryName] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Save state + banner
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [banner, setBanner] = useState(null); // { type: "success"|"error"|"info", message: string }
  const bannerTimerRef = useRef(null);

  // Convert-to-order (per grouped row)
  const [convertOrderId, setConvertOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");

  const roleLower = safeLower(currentUserRole).trim();
  const isAdminOrOwner = !!isAdmin || roleLower === "admin" || roleLower === "owner";
  const canManage = isAdminOrOwner;


  // Auto-hide banner
  useEffect(() => {
    if (!banner) return;
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), BANNER_AUTO_HIDE_MS);
    return () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, [banner]);

  useEffect(() => {
    if (!showActionsMenu) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target?.closest?.("[data-sample-order-actions-wrap]")) return;
      setShowActionsMenu(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowActionsMenu(false);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showActionsMenu]);

  const showBanner = (type, message) => setBanner({ type, message });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const toMillis = (ts) => {
    // Firestore Timestamp
    if (ts && typeof ts === "object") {
      if (typeof ts.toDate === "function") return ts.toDate().getTime();
      if (typeof ts.seconds === "number") return ts.seconds * 1000;
    }
    const d = new Date(ts);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : 0;
  };

  const formatDateShort = (value) => {
    if (!value) return "—";
    const ms = toMillis(value);
    if (!ms) return String(value);
    const d = new Date(ms);
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  };

  const getGroupKey = (r) =>
    r.orderId ||
    r.sampleOrderId ||
    r.requestId ||
    r.sampleRequestId ||
    r.groupId ||
    r.parentId ||
    r.id;

  const getItemsFromAnyShape = (r) => {
    if (Array.isArray(r.items) && r.items.length) return r.items;
    if (Array.isArray(r.lineItems) && r.lineItems.length) return r.lineItems;
    if (Array.isArray(r.products) && r.products.length) return r.products;
    if (Array.isArray(r.sampleItems) && r.sampleItems.length) return r.sampleItems;
    if (Array.isArray(r.orderItems) && r.orderItems.length) return r.orderItems;

    // Fallback: treat request itself as a single item
    return [
      {
        productName: r.productName,
        productCode: r.productCode,
        quantity: r.quantity ?? 1,
        size: r.size,
      },
    ];
  };

  // ✅ Apply visibility filter BEFORE grouping (same as your previous version)
  const visibleSourceRequests = useMemo(() => {
    return filterVisibleSampleOrders(requests, {
      isAdmin: isAdminOrOwner,
      currentUserId,
      currentUserRole,
      currentUserGroupIds,
      currentUserAccountIds,
      includeHidden: isAdminOrOwner && showHidden,
    });
  }, [
    requests,
    isAdminOrOwner,
    currentUserId,
    currentUserRole,
    currentUserGroupIds,
    currentUserAccountIds,
    showHidden,
  ]);

  // 1) GROUP by order (one row per order)
  const groupedOrders = useMemo(() => {
    const map = new Map();

    for (const r of visibleSourceRequests || []) {
      const key = getGroupKey(r);
      if (!key) continue;

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          docIds: [],
          createdAt: r.createdAt ?? null,
          requestedDate: r.requestedDate ?? r.createdAt ?? null,
          accountName: r.accountName ?? null,
          territoryName: r.territoryName ?? r.territory ?? null,
          assignedToId: r.assignedToId ?? null,
          assignedToName: r.assignedToName ?? null,
          status: r.status ?? "Pending",
          hidden: !!r.hidden,
          convertedToOrder: !!r.convertedToOrder,
          orderNumber: r.orderNumber ?? null,
          sourceRequest: r,
          items: [],
        });
      }

      const g = map.get(key);
      g.docIds.push(r.id);

      // earliest createdAt for stable sorting
      const gMs = toMillis(g.createdAt);
      const rMs = toMillis(r.createdAt);
      if (!gMs || (rMs && rMs < gMs)) g.createdAt = r.createdAt ?? g.createdAt;

      if (!g.requestedDate && r.requestedDate) g.requestedDate = r.requestedDate;
      if (!g.accountName && r.accountName) g.accountName = r.accountName;
      if (!g.territoryName && (r.territoryName || r.territory))
        g.territoryName = r.territoryName || r.territory;

      if (!g.assignedToId && r.assignedToId) g.assignedToId = r.assignedToId;
      if (!g.assignedToName && r.assignedToName) g.assignedToName = r.assignedToName;
      if (!g.sourceRequest) g.sourceRequest = r;

      // mixed status check
      const rStatus = r.status ?? "Pending";
      if (g.status !== rStatus && g.status !== "MIXED") g.status = "MIXED";

      if (r.hidden) g.hidden = true;
      if (r.convertedToOrder) {
        g.convertedToOrder = true;
        if (!g.orderNumber && r.orderNumber) g.orderNumber = r.orderNumber;
      }

      const items = getItemsFromAnyShape(r);
      if (Array.isArray(items) && items.length) g.items.push(...items);
    }

    return Array.from(map.values()).map((g) => {
      if (g.docIds.length <= 1 && g.status === "MIXED") g.status = "Pending";
      return g;
    });
  }, [visibleSourceRequests]);

  const exportSampleOrders = async () => {
    try {
      setExporting(true);
      const rows = visibleOrders.map((order) => ({
        Account: order?.accountName || "",
        Status: order?.status || "",
        Territory: order?.territoryName || "",
        Assigned: order?.assignedToName || "",
        Items: getItemCountsForRequest(order)?.label || "",
        Quantity: order?.quantity || order?.totalQuantity || 0,
        "Requested Date": formatDateShort(order?.requestedDate),
        "Delivery Date": formatDateShort(order?.deliveryDate),
        "Order #": order?.orderNumber || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No visible sample orders to export." }]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SampleOrders");
      XLSX.writeFile(wb, "SampleOrdersExport.xlsx");
    } finally {
      setExporting(false);
    }
  };

  // Filter dropdown options
  const accountOptions = useMemo(() => {
    const s = new Set();
    groupedOrders.forEach((g) => {
      if (g.accountName) s.add(g.accountName);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [groupedOrders]);

  const territoryOptions = useMemo(() => {
    const s = new Set();
    groupedOrders.forEach((g) => {
      if (g.territoryName) s.add(g.territoryName);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [groupedOrders]);

  const buildItemsSummary = (g) => {
    const items = Array.isArray(g.items) ? g.items : [];
    if (!items.length) return { text: "—", title: "" };

    const first = items[0];
    const firstName = first?.productName || "Unnamed";
    const extra = Math.max(0, items.length - 1);
    const text = extra > 0 ? `${firstName} +${extra}` : firstName;

    const title = items
      .slice(0, 8)
      .map((it) => {
        const n = it?.productName || "Unnamed";
        const q = it?.quantity ?? 1;
        return `${n} (x${q})`;
      })
      .join(", ");

    return { text, title };
  };

  const calcTotalQty = (g) => {
    const items = Array.isArray(g.items) ? g.items : [];
    return items.reduce((sum, it) => sum + (Number(it?.quantity) || 1), 0);
  };

  // 2) FILTER + SEARCH + SORT (after grouping)
  const visibleOrders = useMemo(() => {
    const needle = safeLower(filters.search).trim();

    const filtered = groupedOrders.filter((g) => {
      if (!showHidden && g.hidden) return false;

      if (filters.account !== "ALL" && (g.accountName || "—") !== filters.account) return false;

      if (filters.territory !== "ALL" && (g.territoryName || "—") !== filters.territory)
        return false;

      // ✅ Status filter (treat MIXED as its own option if user selects it)
      if (filters.status !== "ALL") {
        const s = g.status || "Pending";
        if (filters.status === "MIXED") {
          if (s !== "MIXED") return false;
        } else {
          // if group is MIXED, it does NOT match any single status filter
          if (s === "MIXED") return false;
          if (s !== filters.status) return false;
        }
      }

      if (!needle) return true;

      const itemsSummary = buildItemsSummary(g).text;
      const hay = [
        g.accountName,
        g.territoryName,
        g.assignedToName,
        g.status,
        g.orderNumber,
        itemsSummary,
      ]
        .map(safeLower)
        .join(" | ");

      return hay.includes(needle);
    });

    const sorted = filtered.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];

      if (sortField === "createdAt" || sortField === "requestedDate") {
        aVal = toMillis(aVal);
        bVal = toMillis(bVal);
      } else if (sortField === "itemsSummary") {
        aVal = safeLower(buildItemsSummary(a).text);
        bVal = safeLower(buildItemsSummary(b).text);
      } else {
        aVal = safeLower(aVal);
        bVal = safeLower(bVal);
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [
    groupedOrders,
    showHidden,
    filters.search,
    filters.account,
    filters.territory,
    filters.status,
    sortField,
    sortDirection,
  ]);


  const selectedGroups = useMemo(() => visibleOrders.filter((g) => selectedIds.has(g.id)), [visibleOrders, selectedIds]);

  const bulkStatusOptions = useMemo(() => {
    if (!selectedGroups.length) return STATUS_OPTIONS;
    return intersectStatusOptions(selectedGroups, currentUserGroupIds, canManage);
  }, [selectedGroups, currentUserGroupIds, canManage]);

  const bulkStatusRestrictionNote = useMemo(() => {
    const notes = selectedGroups
      .map((group) => getRestrictedStatusMeta(group.status === "MIXED" ? "" : group.status, currentUserGroupIds, canManage).note)
      .filter(Boolean);
    return Array.from(new Set(notes)).join(" ");
  }, [selectedGroups, currentUserGroupIds, canManage]);

  // Clear selection when any filter changes (InventoryTable behavior)
  useEffect(() => {
    if (selectedIds.size > 0) setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.account, filters.territory, filters.status, showHidden]);

  const selectedCount = selectedIds.size;

  const toggleSelectAllVisible = () => {
    const visibleIds = visibleOrders.map((g) => g.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleRow = (groupId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());
  const closeBulkEdit = () => {
    if (isBulkSaving) return;
    setBulkEditOpen(false);
  };

  /**
   * IMPORTANT: updateGroup RETURNS a Promise that resolves when all doc updates complete.
   */
  const updateGroup = async (group, patch) => {
    if (!group) return { okCount: 0, failCount: 0 };
    if (!canManage) return { okCount: 0, failCount: 0 };

    const ids = Array.isArray(group.docIds) && group.docIds.length ? group.docIds : [];
    if (!ids.length) return { okCount: 0, failCount: 0 };

    const results = await Promise.allSettled(
      ids.map(async (docId) => {
        if (!onUpdateRequest) throw new Error("onUpdateRequest not provided");
        return onUpdateRequest(docId, patch);
      })
    );

    let okCount = 0;
    let failCount = 0;
    results.forEach((r) => {
      if (r.status === "fulfilled") okCount += 1;
      else failCount += 1;
    });

    return { okCount, failCount };
  };

  const applyBulkUpdates = async () => {
    if (!canManage) return;
    if (!selectedIds.size) return;
    if (isBulkSaving) return;

    if (!selectedGroups.length) return;

    const nextAssignedUser = bulkAssignedToId
      ? companyUsers.find((user) => user.id === bulkAssignedToId) || null
      : null;

    const patch = {};
    if (bulkStatus) patch.status = bulkStatus;
    if (bulkTerritoryName === "__clear__") {
      patch.territoryName = null;
      patch.territory = null;
    } else if (bulkTerritoryName) {
      patch.territoryName = bulkTerritoryName;
      patch.territory = bulkTerritoryName;
    }
    if (bulkAssignedToId === "__clear__") {
      patch.assignedToId = null;
      patch.assignedToName = null;
    } else if (bulkAssignedToId) {
      patch.assignedToId = bulkAssignedToId;
      patch.assignedToName = nextAssignedUser ? nextAssignedUser.fullName || nextAssignedUser.email : null;
    }

    if (!Object.keys(patch).length) {
      showBanner("error", "Choose a status, territory, or assignee before applying bulk edit.");
      return;
    }

    if (bulkStatus) {
      const blockedGroup = selectedGroups.find((group) => {
        const meta = getRestrictedStatusMeta(group.status === "MIXED" ? "" : group.status, currentUserGroupIds, canManage);
        return !meta.options.includes(bulkStatus);
      });
      if (blockedGroup) {
        showBanner("error", bulkStatusRestrictionNote || "Your operational group only allows specific status transitions.");
        return;
      }
    }

    setIsBulkSaving(true);
    showBanner(
      "info",
      `Saving ${selectedGroups.length} order${selectedGroups.length === 1 ? "" : "s"}...`
    );

    try {
      const perGroup = await Promise.allSettled(selectedGroups.map((g) => updateGroup(g, patch)));

      let okDocs = 0;
      let failDocs = 0;

      perGroup.forEach((r) => {
        if (r.status === "fulfilled") {
          okDocs += r.value?.okCount || 0;
          failDocs += r.value?.failCount || 0;
        } else {
          failDocs += 1;
        }
      });

      if (failDocs === 0) {
        showBanner("success", `${okDocs} item${okDocs === 1 ? "" : "s"} have been saved.`);
        clearSelection();
        setBulkStatus("");
        setBulkAssignedToId("");
        setBulkTerritoryName("");
        setBulkEditOpen(false);
      } else if (okDocs > 0) {
        showBanner("error", `${okDocs} saved, ${failDocs} failed. Please try again.`);
      } else {
        showBanner("error", `Save failed. Please try again.`);
      }
    } catch (e) {
      showBanner("error", `Save failed. Please try again.`);
      console.error("Bulk sample order update error:", e);
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleConvertToOrder = async (group) => {
    if (!canManage) return;
    if (!orderNumber.trim()) {
      alert("Please enter an order number");
      return;
    }

    setIsBulkSaving(true);
    showBanner("info", "Saving...");

    try {
      const { okCount, failCount } = await updateGroup(group, {
        convertedToOrder: true,
        orderNumber: orderNumber.trim(),
        status: "Converted to Order",
      });

      if (failCount === 0) {
        showBanner("success", `${okCount} item${okCount === 1 ? "" : "s"} have been saved.`);
        setConvertOrderId(null);
        setOrderNumber("");
      } else if (okCount > 0) {
        showBanner("error", `${okCount} saved, ${failCount} failed. Please try again.`);
      } else {
        showBanner("error", "Save failed. Please try again.");
      }
    } catch (e) {
      showBanner("error", "Save failed. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Convert to order error:", e);
    } finally {
      setIsBulkSaving(false);
    }
  };

  const statusPillClass = (status) => {
    if (status === "Approved") return "bg-green-50 text-green-700 border-green-200";
    if (status === "Cancelled") return "bg-red-50 text-red-700 border-red-200";
    if (status === "Converted to Order") return "bg-purple-50 text-purple-700 border-purple-200";
    if (status === "Completed") return "bg-slate-50 text-slate-700 border-slate-200";
    if (status === "MIXED") return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  const allVisibleSelected =
    visibleOrders.length > 0 && visibleOrders.every((g) => selectedIds.has(g.id));

  const bannerStyles =
    banner?.type === "success"
      ? "bg-green-50 border-green-200 text-green-800"
      : banner?.type === "error"
      ? "bg-red-50 border-red-200 text-red-800"
      : "bg-slate-50 border-slate-200 text-slate-800";

  // ✅ Inventory-like FilterChip
  const FilterChip = ({ label, onRemove }) => (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-[12px] text-slate-700">
      <span className="truncate max-w-[220px]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-slate-200 text-slate-600"
        title="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );

  return (
    <div className="sample-order-table-shell bg-white rounded border border-gray-200 overflow-hidden w-full">
      {/* Non-click banner */}
      {banner && (
        <div className={`px-3 py-2 border-b text-xs ${bannerStyles}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="truncate">{banner.message}</span>
            {isBulkSaving ? (
              <span className="text-[11px] opacity-70 whitespace-nowrap">Saving…</span>
            ) : null}
          </div>
        </div>
      )}

      {/* Bulk action toolbar */}
      {canManage && selectedCount > 0 && (
        <div className="p-3 border-b border-blue-200 bg-blue-50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
              <CheckSquare className="w-4 h-4 text-blue-700" />
              <span>{selectedCount} order{selectedCount === 1 ? "" : "s"} selected</span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setBulkEditOpen(true)}
                disabled={isBulkSaving}
                className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors ${
                  isBulkSaving
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Bulk Edit
              </button>
              <button
                type="button"
                onClick={clearSelection}
                disabled={isBulkSaving}
                className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors ${
                  isBulkSaving
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Slim header row: Search + Filters toggle + admin hidden toggle */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search always visible */}
          <div className="flex-1 min-w-[240px]">
            <input
              type="text"
              placeholder="Search (account, territory, assigned, item, order#)"
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </div>

          {/* Filters toggle (InventoryTable style) */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors whitespace-nowrap ${
              showFilters || (hasAnyFilter && !showFilters)
                ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
            title="Show/hide filters"
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 ? (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[11px]">
                {activeFilterCount}
              </span>
            ) : null}
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Admin-only show hidden */}
          {isAdminOrOwner && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none whitespace-nowrap">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show hidden
            </label>
          )}

          <div className="relative" data-sample-order-actions-wrap>
            <button
              type="button"
              onClick={() => setShowActionsMenu((prev) => !prev)}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 whitespace-nowrap"
            >
              Actions
              <ChevronDown className="w-4 h-4" />
            </button>

            {showActionsMenu ? (
              <div className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                <button
                  type="button"
                  onClick={async () => {
                    setShowActionsMenu(false);
                    await exportSampleOrders();
                  }}
                  disabled={exporting}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Download className="w-4 h-4 text-slate-500" />
                  {exporting ? "Exporting…" : "Export Sample Orders"}
                </button>
              </div>
            ) : null}
          </div>

          <div className="text-xs text-gray-500 whitespace-nowrap ml-auto">
            {visibleOrders.length} order{visibleOrders.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* ✅ Active filter chips (only show when active) */}
        {(filters.account !== "ALL" || filters.territory !== "ALL" || filters.status !== "ALL") && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {filters.account !== "ALL" ? (
              <FilterChip
                label={`Account: ${filters.account}`}
                onRemove={() => setFilters((p) => ({ ...p, account: "ALL" }))}
              />
            ) : null}

            {filters.territory !== "ALL" ? (
              <FilterChip
                label={`Territory: ${filters.territory}`}
                onRemove={() => setFilters((p) => ({ ...p, territory: "ALL" }))}
              />
            ) : null}

            {filters.status !== "ALL" ? (
              <FilterChip
                label={`Status: ${filters.status}`}
                onRemove={() => setFilters((p) => ({ ...p, status: "ALL" }))}
              />
            ) : null}

            <button
              type="button"
              onClick={() => {
                resetFilters();
                setShowFilters(false);
              }}
              className="text-[12px] font-medium text-blue-700 hover:text-blue-900 whitespace-nowrap"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ✅ Collapsible filter panel */}
      {showFilters && (
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-900">Filters</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-medium text-blue-700 hover:text-blue-900"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
              >
                <X className="w-3.5 h-3.5" />
                Close
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Account */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.account}
                onChange={(e) => setFilters((prev) => ({ ...prev, account: e.target.value }))}
              >
                <option value="ALL">All Accounts</option>
                {accountOptions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            {/* Territory */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Territory</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.territory}
                onChange={(e) => setFilters((prev) => ({ ...prev, territory: e.target.value }))}
              >
                <option value="ALL">All Territories</option>
                {territoryOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ Status */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="ALL">All Statuses</option>
                {/* include MIXED for filtering */}
                <option value="MIXED">Mixed</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Helpful quick reset (optional) */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={resetFilters}
                className="w-full h-[42px] rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto w-full">
        <table className="w-full text-xs table-fixed">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="whitespace-nowrap select-none">
              {/* Select */}
              <th className="px-2 py-2 w-[34px]">
                {canManage ? (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-gray-300"
                    title="Select all visible"
                    disabled={isBulkSaving}
                  />
                ) : null}
              </th>

              {/* ✅ Account FIRST */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[210px]"
                onClick={() => handleSort("accountName")}
                title="Sort by account"
              >
                <div className="flex items-center gap-1">
                  Account
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Items summary */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[260px]"
                onClick={() => handleSort("itemsSummary")}
                title="Sort by items"
              >
                <div className="flex items-center gap-1">
                  Items
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Total Qty */}
              <th className="px-2 py-2 text-left font-medium text-gray-700 w-[56px]">Qty</th>

              {/* Territory */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[150px]"
                onClick={() => handleSort("territoryName")}
                title="Sort by territory"
              >
                <div className="flex items-center gap-1">
                  Territory
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Assigned */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[190px]"
                onClick={() => handleSort("assignedToName")}
                title="Sort by assigned user"
              >
                <div className="flex items-center gap-1">
                  Assigned
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Status */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[220px]"
                onClick={() => handleSort("status")}
                title="Sort by status"
              >
                <div className="flex items-center gap-1">
                  Status
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Date */}
              <th
                className="px-2 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-[86px]"
                onClick={() => handleSort("requestedDate")}
                title="Sort by date"
              >
                <div className="flex items-center gap-1">
                  Date
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>

              {/* Convert */}
              <th className="px-2 py-2 text-left font-medium text-gray-700 w-[190px]">
                Convert
              </th>

              {/* Actions */}
              <th className="px-2 py-2 text-center font-medium text-gray-700 w-[64px]">Act</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {visibleOrders.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-3 py-6 text-center text-gray-500">
                  No sample orders found
                </td>
              </tr>
            ) : (
              visibleOrders.map((group) => {
                const selected = selectedIds.has(group.id);
                const { extraItems } = getItemCountsForRequest(group);
                const totalQty = calcTotalQty(group);
                const itemsSummary = buildItemsSummary(group);

                const showHideToggle =
                  (group.status === "Cancelled" || group.status === "MIXED") && canManage;

                return (
                  <tr
                    key={group.id}
                    className={`hover:bg-gray-50 whitespace-nowrap ${
                      group.hidden ? "opacity-50" : ""
                    } ${selected ? "bg-blue-50" : ""}`}
                  >
                    {/* Select */}
                    <td className="px-2 py-1.5 align-middle">
                      {canManage ? (
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(group.id)}
                          className="rounded border-gray-300"
                          disabled={isBulkSaving}
                        />
                      ) : null}
                    </td>

                    {/* ✅ Account (FIRST) */}
                    <td className="px-2 py-1.5 align-middle text-gray-700 truncate">
                      <span title={group.accountName || "—"}>{group.accountName || "—"}</span>
                    </td>

                    {/* Items */}
                    <td className="px-2 py-1.5 align-middle">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="font-medium text-gray-900 truncate block max-w-[220px]"
                          title={itemsSummary.title || itemsSummary.text}
                        >
                          {itemsSummary.text}
                        </span>

                        {extraItems > 0 && (
                          <span
                            className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                            title={`This order has ${extraItems + 1} items total`}
                          >
                            +{extraItems}
                          </span>
                        )}

                        {group.convertedToOrder && (
                          <ShoppingCart
                            className="w-3.5 h-3.5 text-purple-600 flex-shrink-0"
                            title="Converted to Order"
                          />
                        )}
                      </div>
                    </td>

                    {/* Qty */}
                    <td className="px-2 py-1.5 align-middle text-gray-700 tabular-nums">
                      {totalQty || "—"}
                    </td>

                    {/* Territory */}
                    <td className="px-2 py-1.5 align-middle text-gray-700 truncate">
                      <span title={group.territoryName || "—"}>{group.territoryName || "—"}</span>
                    </td>

                    {/* Assigned */}
                    <td className="px-2 py-1.5 align-middle">
                      {canManage ? (
                        <select
                          className="text-xs border border-gray-200 rounded px-2 py-1 w-full min-w-[180px] bg-white"
                          value={group.assignedToId || ""}
                          onChange={async (e) => {
                            const userId = e.target.value;
                            const user = companyUsers.find((u) => u.id === userId) || null;

                            setIsBulkSaving(true);
                            showBanner("info", "Saving...");

                            try {
                              const { okCount, failCount } = await updateGroup(group, {
                                assignedToId: userId || null,
                                assignedToName: user ? user.fullName || user.email : null,
                              });

                              if (failCount === 0) {
                                showBanner(
                                  "success",
                                  `${okCount} item${okCount === 1 ? "" : "s"} have been saved.`
                                );
                              } else if (okCount > 0) {
                                showBanner("error", `${okCount} saved, ${failCount} failed. Please try again.`);
                              } else {
                                showBanner("error", "Save failed. Please try again.");
                              }
                            } catch (err) {
                              showBanner("error", "Save failed. Please try again.");
                              // eslint-disable-next-line no-console
                              console.error("Assigned update error:", err);
                            } finally {
                              setIsBulkSaving(false);
                            }
                          }}
                          title="Assign user (applies to whole order)"
                          disabled={isBulkSaving}
                        >
                          <option value="">Not assigned</option>
                          {companyUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.fullName || user.email}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-700 truncate block" title={group.assignedToName || "—"}>
                          {group.assignedToName || "—"}
                        </span>
                      )}
                    </td>

                    {/* ✅ Status (WIDER + can filter by status above) */}
                    <td className="px-2 py-1.5 align-middle">
                      {canManage ? (() => {
                        const statusMeta = getRestrictedStatusMeta(group.status === "MIXED" ? "" : group.status, currentUserGroupIds, canManage);
                        const currentValue = statusMeta.options.includes(group.status) ? group.status : statusMeta.options[0] || group.status || "Pending";
                        return (
                          <select
                            className="text-xs border border-gray-200 rounded px-2 py-1 w-full min-w-[210px] bg-white"
                            value={currentValue}
                            onChange={async (e) => {
                              if (statusMeta.disabled) return;
                              setIsBulkSaving(true);
                              showBanner("info", "Saving...");

                              try {
                                const { okCount, failCount } = await updateGroup(group, {
                                  status: e.target.value,
                                });

                                if (failCount === 0) {
                                  showBanner(
                                    "success",
                                    `${okCount} item${okCount === 1 ? "" : "s"} have been saved.`
                                  );
                                } else if (okCount > 0) {
                                  showBanner("error", `${okCount} saved, ${failCount} failed. Please try again.`);
                                } else {
                                  showBanner("error", "Save failed. Please try again.");
                                }
                              } catch (err) {
                                showBanner("error", "Save failed. Please try again.");
                                console.error("Status update error:", err);
                              } finally {
                                setIsBulkSaving(false);
                              }
                            }}
                            title={statusMeta.note || "Status (applies to whole order)"}
                            disabled={isBulkSaving || statusMeta.disabled}
                          >
                            {statusMeta.options.map((status) => (
                              <option key={status} value={status}>
                                {getSampleOrderStatusLabel(status, sampleStatusLabelSettings)}
                              </option>
                            ))}
                          </select>
                        );
                      })() : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${statusPillClass(
                            group.status || "Pending"
                          )}`}
                          title={group.status === "MIXED" ? "Mixed statuses in this grouped order" : ""}
                        >
                          {group.status === "MIXED"
                            ? "Mixed"
                            : getSampleOrderStatusLabel(group.status || "Pending", sampleStatusLabelSettings)}
                        </span>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-2 py-1.5 align-middle text-gray-700 tabular-nums">
                      {formatDateShort(group.requestedDate)}
                    </td>

                    {/* Convert */}
                    <td className="px-2 py-1.5 align-middle">
                      {group.convertedToOrder ? (
                        <div
                          className="flex items-center gap-1 text-purple-600 text-[11px] truncate"
                          title={group.orderNumber || ""}
                        >
                          <ShoppingCart className="w-3 h-3" />
                          <span className="truncate">{group.orderNumber || "Converted"}</span>
                        </div>
                      ) : canManage ? (
                        convertOrderId === group.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              placeholder="Order #"
                              className="text-xs border border-gray-300 rounded px-2 py-1 w-[110px]"
                              value={orderNumber}
                              onChange={(e) => setOrderNumber(e.target.value)}
                              autoFocus
                              disabled={isBulkSaving}
                            />
                            <button
                              onClick={() => handleConvertToOrder(group)}
                              className={`text-xs px-2 py-1 rounded ${
                                isBulkSaving
                                  ? "bg-purple-300 text-white cursor-not-allowed"
                                  : "bg-purple-600 text-white hover:bg-purple-700"
                              }`}
                              title="Save"
                              disabled={isBulkSaving}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setConvertOrderId(null);
                                setOrderNumber("");
                              }}
                              className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              title="Cancel"
                              disabled={isBulkSaving}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConvertOrderId(group.id)}
                            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            disabled={isBulkSaving}
                          >
                            Convert
                          </button>
                        )
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-1.5 align-middle text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => onSelectRequest && onSelectRequest(group.sourceRequest || group)}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50"
                          title="Edit sample order"
                        >
                          <SquarePen className="w-4 h-4" />
                        </button>
                        {showHideToggle ? (
                          <button
                            onClick={async () => {
                              setIsBulkSaving(true);
                              showBanner("info", "Saving...");

                              try {
                                const { okCount, failCount } = await updateGroup(group, {
                                  hidden: !group.hidden,
                                });

                                if (failCount === 0) {
                                  showBanner(
                                    "success",
                                    `${okCount} item${okCount === 1 ? "" : "s"} have been saved.`
                                  );
                                } else if (okCount > 0) {
                                  showBanner("error", `${okCount} saved, ${failCount} failed. Please try again.`);
                                } else {
                                  showBanner("error", "Save failed. Please try again.");
                                }
                              } catch (err) {
                                showBanner("error", "Save failed. Please try again.");
                                console.error("Hidden update error:", err);
                              } finally {
                                setIsBulkSaving(false);
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                            title={group.hidden ? "Show" : "Hide"}
                            disabled={isBulkSaving}
                          >
                            {group.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {bulkEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Bulk Edit Sample Orders</div>
                <div className="text-xs text-slate-500 mt-1">
                  Update status, territory, and assignment for {selectedCount} selected order{selectedCount === 1 ? "" : "s"}.
                </div>
              </div>

              <button
                type="button"
                onClick={closeBulkEdit}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Status</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current status</option>
                  {bulkStatusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                {bulkStatusRestrictionNote ? (
                  <div className="mt-2 text-xs text-slate-500">{bulkStatusRestrictionNote}</div>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Territory</label>
                <select
                  value={bulkTerritoryName}
                  onChange={(e) => setBulkTerritoryName(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current territory</option>
                  <option value="__clear__">Clear territory</option>
                  {territoryOptions.map((territory) => (
                    <option key={territory} value={territory}>{territory}</option>
                  ))}
                </select>
                {bulkStatusRestrictionNote ? (
                  <div className="mt-2 text-xs text-slate-500">{bulkStatusRestrictionNote}</div>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Assigned user</label>
                <select
                  value={bulkAssignedToId}
                  onChange={(e) => setBulkAssignedToId(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-700"
                >
                  <option value="">Keep current assignee</option>
                  <option value="__clear__">Clear current assignee</option>
                  {companyUsers.map((user) => (
                    <option key={user.id} value={user.id}>{user.fullName || user.email}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={closeBulkEdit}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isBulkSaving}
                onClick={applyBulkUpdates}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {isBulkSaving ? "Saving…" : "Save Bulk Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">
        Showing {visibleOrders.length} order{visibleOrders.length === 1 ? "" : "s"}
        {canManage ? " • Use checkboxes to bulk-change status" : ""}
      </div>
    </div>
  );
};

export default SampleOrderTable;
